/* Ported from the mockup project's data/stats.js (2026-09-24).
   If the app already holds Class 2 strings in one module, import those instead
   and delete the `quoted` block here — two copies is how strings drift.

   Single source of truth for every number that appears on the marketing site.
   Swapping placeholder figures for real bake-off results is a data edit here —
   never a copy hunt across pages. Every stat renders through components/marketing/Stat.jsx.

   Copy standards, 2026-08-28: no string here may call a business invisible,
   failing or losing. It was not named in an answer; that is all we observed.
   Every number carries its denominator. */
import { I, by, totals, count, word, said } from '~/marketing/research/figures';

export const AEO_DATA = {
  brand: { name: "Spruce", domain: "spruce-aeo.com", sender: "Spruce <hello@spruce-aeo.com>" },

  /* The question set, as the site states it.

     Added 2026-10-05 for the FAQ, which quotes `questions_basis` and has to fill
     its two slots. These are facts about `config/verticals.yml` — every vertical
     writes 20 questions in an 8/4/6/2 intent shape, and the 10 on commercial
     intents (discovery and reputation) are what `Visibility` counts. The
     marketing site cannot read Ruby, so `marketing_claims_test` reads these back
     against `PromptTemplates` and `Prompt::COMMERCIAL_INTENTS`. */
  questions: { total: 20, commercial: 10 },

  /* The trial, as the site states it. `Account.trial_days` is what actually ends
     one and is deliberately a parameter rather than a constant, so this is the
     number most likely to be tuned without anybody thinking about the marketing
     copy. `marketing_claims_test` compares them. */
  trial: { days: 14 },

  /* The three tiers, as the site states them.

     **Moved here from Pricing.jsx on 2026-10-05 because a second reader
     arrived**: the SoftwareApplication JSON-LD publishes these as `offers`, and
     an offer is a machine-readable price claim. Two copies of a price is how a
     site advertises one number and a checkout charges another — so there is one
     copy, and `structured_data_test` reads it back against `Billing::Plans`,
     which is what actually charges. That test is the guard; this comment is not.

     `monthly` is dollars because that is how the page prints it. Annual is not
     stated here: it is ten months for twelve, derived everywhere from this
     number, exactly as `Billing::Plans#annual_cents` derives it. */
  plans: [
    { key: "starter", name: "Starter", tag: "1 business", monthly: 79, count: "1", unit: "business or location", allowance: "1,000", queries: 1000, businesses: 1, who: "One business in one city. The owner who wants to stop guessing." },
    { key: "growth", name: "Growth", tag: "6 businesses", monthly: 299, count: "6", unit: "businesses or locations", allowance: "6,000", queries: 6000, businesses: 6, who: "A multi-location contractor, or a small marketing agency with a handful of clients.", focal: true },
    { key: "agency", name: "Agency", tag: "15 businesses", monthly: 699, count: "15", unit: "businesses or locations", allowance: "15,000", queries: 15000, businesses: 15, who: "A marketing agency reporting to fifteen clients." },
  ],

  /* The five engines we query. Never name an engine we do not query. */
  engines: ["ChatGPT", "Claude", "Gemini", "Perplexity", "Google AI Overviews"],

  /* Class 2 — quoted. Written once in the copy standards, used verbatim,
     never paraphrased. If one reads badly in place, that is a change request
     against the standards file, not a local wording decision. */
  quoted: {
    measurement_basis:
      "We ask each AI assistant the same questions a customer would, through an API, so the same question asked next week is comparable to this week's. That repeatability is what makes a trend line mean something. An API answer is usually more concise than the one someone sees in the app, and cites fewer sources, so read a count here as the pattern across many answers rather than a record of one person's screen.",
    variability:
      "Ask an AI assistant the same question twice and the answer changes. Measured across 1,622 answers collected on 18 and 19 Aug 2026: when one answer names a business, asking again names it again about four times in five on Perplexity and about half the time on ChatGPT. That is why every question is asked three times and every answer is counted. One answer on its own can mislead; three make the count reflect what each AI assistant does.",
    engine_coverage:
      "We measure ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews.",
    // The research page's two limits, proposed 2026-10-12 and awaiting the
    // founder — see copy-rules.md.
    benchmark_scope:
      "Measured on Perplexity, Gemini and Google AI Overviews, in Dallas, Nashville and Boise.",
    benchmark_franchise_names:
      "Counted by brand, a franchise named with its city, as in \"… of Boise\", is added to the same brand named alone, wherever the brand is also named on its own in that city. Counted as written, each spelling is a separate business.",
    google_method:
      "Every question we ask names the city you're tracking, on all five AI assistants. Google also tailors its results to where the person searching is located, so for Google we run the search from that city as well, and read the AI Overview it returns.",
    google_none:
      "Google shows an AI Overview for some searches and not others. A search without one is recorded as its own outcome and kept out of your denominator, so only searches that had an answer count toward your results.",
    sampling:
      "Every question is asked three times, every week, because one answer on its own can mislead, and weekly is often enough to see a real change. ChatGPT and Claude are asked the four questions where someone is choosing who to call, rather than all twenty. Measuring all five AI assistants on the same schedule matters more than asking each of them every question.",
    weekly_not_daily:
      "We measure weekly rather than daily. Answers vary enough between identical questions that a daily chart would mostly show the AI assistant changing its mind rather than a change in your visibility.",
    reading_moves:
      "Answers vary between identical questions, so a reading can move from one week to the next even when nothing about your business has changed. A move that holds across several readings is a real one, and that is how to tell whether something you did is working.",

    /* The three plan rows. Sanctioned 2026-09-18 as short forms of the three
       strings above, gated on the scheduler, and released 2026-10-05.

       **Added here 2026-10-09 because they had no source at all**, which is
       worse than a second copy: they were declared in `Pricing.jsx` and nowhere
       else, so `copy_rules_test` had never compared them against anything and a
       reword on either side would have gone unnoticed in both directions. Three
       sanctioned claims — which engines run, how many samples, how often — on
       the page where somebody decides what to pay, unguarded for four days.

       The lead-sentence form of the engine row is sanctioned too and is
       deliberately absent: nothing renders it. A key nothing reads is the
       `brands.aliases` shape — built, indexed, consumed by nothing — and the
       guard here only earns its keep over copy that actually ships. */
    engine_coverage_row:
      "ChatGPT, Claude, Gemini, Perplexity and Google AI Overviews — every plan, every run",
    sampling_row: "Three samples per question, so each reading reflects more than one answer",
    cadence_row: "Weekly refresh, with the measurement date on every figure",

    /* Sanctioned 2026-10-05 for the FAQ. All three are claims about what is
       measured and how, which is what makes them Class 2 rather than page copy. */
    denominator_basis:
      "The figure Spruce reports, \"named in 2 of 4 answers that recommended a business\", counts only those answers. When an answer recommends no business, there was nothing to be named in, so it is reported on its own rather than counted against your results.",
    name_matching:
      "You give us the business name, and that is what every answer is matched against. Legal suffixes, punctuation, casing and spacing are set aside, and a longer or shorter form of the same name still counts — so \"Radiance Med Spa, LLC\", \"Radiance MedSpa\" and \"Radiance\" are all read as the same business. If customers know you by another name as well, you can add it at any time, and an answer naming any of them counts.",
    no_website:
      "Many local businesses do not have a website, and one isn't needed — the questions are about your business name rather than your website.",

    citation_measurement:
      "Add a website and we can also measure whether pages on it are being used as sources for the answers. That is a different finding from being named, with its own denominator: a business can be named without being cited (an AI assistant recommends you from a directory rather than from your own pages), and cited without being named (an answer draws on one of your pages without naming you). Without a website, that measurement reads as unknown rather than as a zero.",
    /* The before-purchase form of the annual refund rule. Sanctioned 2026-10-01
       and rendering on `/billing`; added here 2026-10-05 because the FAQ answers
       "what does it cost" and the crossover belongs in front of somebody
       deciding, which is the risk the policy recorded for itself — that meeting
       the month-ten rule in month eleven reads as a trick. */
    annual_before_purchase:
      "An annual plan is billed once a year, up front: twelve months for the price of ten, so the last two months are free. Cancel early and you are refunded what you paid, less the months you used at the monthly price; after month ten there is nothing left to refund.",
    /* Sanctioned 2026-10-05 for the grader. The first is a limit and a
       commitment about what the free check is; the second describes how the
       report's privacy actually works, which is why it names forwarding rather
       than promising more than a token can carry. */
    free_check: "The check is free and stays free. A business can be checked once every 60 days, so you can run it again after you've made a change.",
    report_private: "The report opens only from the link in your email. Nobody can find it without that link, and anyone you forward the email to can open it.",
    not_named_one: "Not named in this answer.",
    no_overview: "Google did not show an AI Overview for this search.",
    single_sample:
      "This is one question, asked once of each AI assistant — a snapshot of today. A plan asks up to twenty questions of each AI assistant, three times each, every week, which is what turns a snapshot into a trend.",
    /* Templated, not literal. These two carried "1 October", "Starter", "1
       business" and "3" as baked-in example values, so quoting them verbatim —
       the only way Class 2 may be used — tells a customer in March that
       collection is paused until 1 October.

       `paused_count` needs the singular because Starter covers exactly one
       business, so "covers 1 businesses" is the most common rendering rather
       than an edge case: the "1 answers" defect in a second string. Neither
       renders anywhere yet; the screens that quote them are batch 3. */
    paused_allowance: (date) =>
      "Collection is paused until " + date + ". You have used this month's allowance.",
    paused_count: (date, plan, included, tracked) =>
      "Collection is paused until " + date + ". " + plan +
      (included === 1 ? " covers one business; you are tracking " : " covers " + included + " businesses; you are tracking ") +
      tracked + ".",
    deletion:
      "Deleting your account removes your businesses, your questions, and every answer we collected for them. Two things are kept. We keep a global list of which websites get cited in AI answers; it contains no personal data and no link to your account. If you ran a free check before signing up, we keep the email address and company name you entered, so the limit of one free check every 60 days still holds.",
    /* The only place the retention window is stated. `Billing::Summary::RETENTION`
       holds the same string for the signed-in side and `Retention::PurgeCancelled::WINDOW`
       is what enforces it; `purge_cancelled_test` fails if the spelled number and
       the swept one stop agreeing. */
    retention:
      "Collected answers are kept while your plan runs, and for twelve months after it ends. After that we remove them. Your businesses and their questions stay, so coming back picks up where you left off rather than starting again.",
    /* Adding another name, on business settings. Sanctioned 2026-10-09.

       Templated because the number and the names are the customer's own,
       computed from the answers they already hold — an illustration here would
       be the one thing this sentence must not be, since its whole job is to
       make a bad name obvious before it is saved.

       The singular has its own form for the reason `recommended_no_one` does:
       one business is the ordinary case, because a customer usually adds a name
       having noticed one spelling. */
    alias_claims: (count, names) =>
      count === 1
        ? "One business already named in your answers would now be counted as you: " + names + "."
        : count + " businesses already named in your answers would now be counted as you: " + names + ".",
    /* The removal direction, sanctioned 2026-10-10 after shipping unsanctioned
       for a day as "One business counted as you would stop being counted."

       **One word differs from `alias_claims` above** — "now" becomes "no
       longer" — and that is the argument for the wording rather than a
       coincidence of it: a reader who has seen one form understands the other
       immediately, and a drift in either is visible by comparison. That is
       cheaper than anything a test could assert about two independently worded
       sentences.

       It names the businesses for the same reason its mirror does, and the
       reason is sharper here: the names dropping off are the customer's own
       former spellings, so seeing them is how somebody catches having deleted
       the wrong line. A count alone cannot tell "I removed a name I no longer
       trade under" from "I removed the wrong one".

       The direction the figure moves is deliberately absent — `alias_recompute`
       covers both directions, and a second claim here would rot independently
       of the first, which is the mistake `name_matching` made three times. */
    alias_releases: (count, names) =>
      count === 1
        ? "One business already named in your answers would no longer be counted as you: " + names + "."
        : count + " businesses already named in your answers would no longer be counted as you: " + names + ".",
    alias_recompute:
      "Saving re-reads every answer we have already collected for this business, so your figures change to reflect these names rather than only future collections.",
    /* Reported beside a summary count, never folded into it.

       The singular is sanctioned and was granted on 2026-09-24; implementing it
       was blocked until 2026-10-09 on the *format* of the authority rather than
       on the words, because `copy-rules.md` carried it in bold and
       `copy_rules_test` parses blockquotes only. So the verbatim form would have
       failed the guard against the file sanctioning it. It is a blockquote now.

       `n` of exactly 1 is ordinary rather than an edge case: a grader check is
       one question across five engines, and on a brand's weekly run one answer
       recommending nobody is a common week. "1 answers" is the same defect
       `paused_count` branches for one entry above. */
    recommended_no_one: (n) =>
      n === 1
        ? "One answer recommended no business at all. There was nothing to be named in, so it is not counted above."
        : n + " answers recommended no business at all. There was nothing to be named in, so they are not counted above.",
    questions_basis: (total, commercial) =>
      "We track " + total + " questions for each business. The " + commercial + " that ask for a recommendation are the ones your figure is built from. The rest ask how to choose and what things cost — and where those name a company, it is as an example rather than a recommendation. Counting them would change your figure without changing how often you are recommended.",
    trial_ended:
      "When the trial ends, collection stops. Everything collected during it stays here and stays readable. Starting a plan resumes collection; the weeks in between stay empty, because nothing was measured then.",
    payment_failed:
      "Collection is paused. Everything already collected stays readable. Collection resumes when the payment goes through.",
    /* Describes the event. "Nothing is deleted unless you delete it." was
       withdrawn 2026-10-09 — scheduling the purge makes it false twelve months
       after a plan ends. The window belongs to `retention` above and to nothing
       else. */
    cancelling:
      "Canceling stops collection. Everything already collected stays here and stays readable — your answers, your questions, and every finding built from them."
  },

  /* Denominators are computed, never constants. The total is answers
     GENERATED — a Google search that returned no AI Overview is not an
     answer a business could have been named in. */
  fmt: {
    named: (n, total) => "Named in " + n + " of " + total + " answers that recommended a business.",
    notNamedNone: (total) => "Not named in any of the " + total + " answers that recommended a business.",
    cardHeadline: (business, n, total) => business + " appears in " + n + " of " + total + " AI answers",
    /* Monitoring card: one line. Only true where every question is asked three
       times, weekly — never on a free-check card. */
    cardCaveat: (date) => "Measured via API · " + date + " · counted across every answer collected",
    /* Free-check card: two lines. A card circulates without its page, so the
       scope travels inside the image — "answers vary" alone would read as a
       survey to anyone who never saw the report. */
    cardCaveatFreeCheck: (date) => [
      "Measured via API · " + date,
      "One question, asked once of each AI assistant · Answers vary between runs"
    ]
  },

  stats: {
    /* `repeat_agreement`, the variability claim as a tile, went 2026-10-26:
       the methodology page was its one reader, and printed the same two
       figures again in the sanctioned paragraph directly under it. Its
       sanctioned short form stays in copy-rules.md for a surface that needs
       one. */
    /* `corpus`, `api_words` and `api_sources` went 2026-10-26 with the tiles
       that drew them: each a figure with no date and no AI assistant named,
       on pages saying every figure carries both. The numbers stay in the
       sanctioned `measurement_basis` and `variability` sentences. */
  }
};

/* SCHEDULER-DEPENDENT copy renders only when this is true. The rows it gates
   describe weekly, three-sample collection across all five engines.

   **True since 2026-10-05, and it should have been flipped on 2026-09-29.**
   `Collection::ScheduledRun` was built 2026-09-25 and `collect_due_brands` has
   fired daily in production since the deploy on the 29th, so for six days the
   pricing page hid three claims that were true: five engines every run, three
   samples a question, and a weekly refresh dated on every figure. Found while
   drafting the FAQ, because the most natural question to ask a measurement
   product is how often it measures and the answer was gated off the site.

   **The lesson is in PROJECT_STATE already and this is its second instance**: a
   flag gated on somebody else's future work needs a check that the gate still
   exists, because nothing fails when the gate opens. `marketing_claims_test.rb`
   is that check now — it reads `config/recurring.yml` and fails if the scheduler
   is running while this says it is not.

   Each claim was verified against the code before being published rather than
   inferred from the scheduler existing. Three samples is
   `Collection::Coverage::SAMPLES`; the dated figure is the `measured_on` prop
   the dashboard renders beside each one. **One gated line did not survive that
   check** — the trade pages promised "we re-run the set and show the delta", and
   there is no delta, trend or history anywhere in the signed-in app. It now
   claims the cadence and the date, which are the parts that are true. */
/* The benchmark's figures on the home page banner and the methodology page,
   **read from `benchmark.json` since 2026-10-14** rather than typed. They were
   typed from the 18 Sep bake-off, and three had drifted: "every one of the 220
   HVAC answers we collected" meant the who-to-hire answers, 220 of 477; "not
   one declined to recommend someone" was true of four industries and is not of
   ten; and "238 different plumbing companies" is a count the file does not
   hold. Each note says when and on what, through the sanctioned scope string,
   rather than "ChatGPT and Claude were not in this run", which the founder had
   taken off the benchmark post for the same reason. */
const hireNote = (what, i) => `${what}, collected ${i.measured_on}. ${AEO_DATA.quoted.benchmark_scope}`;
const busiest = i => i.cities.reduce((a, c) => (c.brands > a.brands ? c : a));
Object.assign(AEO_DATA.stats, {
  hvac_named: {
    status: "measured",
    value: count(by.hvac.named.of),
    before: "Every one of the ",
    after: " HVAC answers asking who to hire named a specific company. The only question is which one.",
    note: hireNote(`${count(by.hvac.named.count)} of ${count(by.hvac.named.of)} who-to-hire answers, HVAC`, by.hvac),
  },
  plumbing_field: {
    status: "measured",
    value: count(busiest(by.plumbing).brands),
    before: "",
    after: ` different plumbers were named in ${busiest(by.plumbing).city} alone. On average, an answer to who to hire names about ${word(Math.round(by.plumbing.named_per_answer))}.`,
    // As long as the other two notes, so the banner does not change height as
    // it rotates. The "about five" is the plumbing page's 5.2.
    note: hireNote(`Plumbing, all ${word(AEO_DATA.questions.total)} questions, by brand`, by.plumbing),
  },
  all_industries: {
    status: "measured",
    value: `${count(totals.named)} of ${count(totals.asked)}`,
    before: "",
    after: ` answers asking who to hire named a specific business, across ${word(I.length)} local industries.`,
    note: `Who-to-hire questions in ${word(I.length)} industries, collected ${said.dates}. ${AEO_DATA.quoted.benchmark_scope}`,
  },
  // Two professions, added on the founder's call the same day. Family law is
  // stated as the high rate it is, not as the lowest of ten, which it also is.
  family_law_named: {
    status: "measured",
    value: `${count(by.family_law.named.count)} of ${count(by.family_law.named.of)}`,
    before: "Asked which family lawyer to hire, AI assistants named a specific one in ",
    after: " answers.",
    note: hireNote("Who-to-hire questions, family law", by.family_law),
  },
  // It ended "more often than in any other industry we measured" until dental
  // was added the same day and edged it, 28.4% to 28.0%: too close to claim
  // either way, so the figure stands on its own.
  chiropractic_advice: {
    status: "measured",
    value: `${count(by.chiropractic.advice.count)} of ${count(by.chiropractic.advice.of)}`,
    before: "Even asked how to choose or what it costs, AI assistants named a specific chiropractor in ",
    after: " answers.",
    note: hireNote("How-to-choose and cost questions, chiropractic", by.chiropractic),
  },
  hvac_sample: {
    status: "measured", value: `${count(by.hvac.named.count)} of ${count(by.hvac.named.of)}`,
    after: " HVAC answers asking who to hire named a specific company",
    note: hireNote("Who-to-hire questions", by.hvac),
  },
  plumbing_sample: {
    status: "measured", value: `${count(by.plumbing.named.count)} of ${count(by.plumbing.named.of)}`,
    after: " plumbing answers asking who to hire named a specific company",
    note: hireNote("Who-to-hire questions", by.plumbing),
  },
});

export const SCHEDULER_LIVE = true;
export const stats = AEO_DATA.stats, quoted = AEO_DATA.quoted, fmt = AEO_DATA.fmt;
